import { useState } from 'react';
import { format } from 'date-fns';
import {
  useTheme,
  Box,
  Typography,
  Divider,
  IconButton,
  Card,
  CardContent,
  CardActions,
  Collapse,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore,
  CheckCircle,
  Cancel,
  HourglassEmpty,
  CalendarMonth,
} from '@mui/icons-material';


const STATUS_CONFIG = {
  Approved: { chipColor: 'success', icon: <CheckCircle fontSize="small" /> },
  Rejected: { chipColor: 'error',   icon: <Cancel fontSize="small" /> },
  Pending:  { chipColor: 'warning', icon: <HourglassEmpty fontSize="small" /> },
};


const RequestCard = ({ request, onClick }) => {
  const theme = useTheme();

  const statusKey = request?.status?.status || 'Pending';
  const statusCfg = STATUS_CONFIG[statusKey] ?? STATUS_CONFIG.Pending;
  const borderColor = theme.palette[statusCfg.chipColor]?.main ?? theme.palette.warning.main;
  const bandBg = borderColor + '22';

  const [commentsExpanded, setCommentsExpanded] = useState(false);
  const comments = Array.isArray(request.comments) ? request.comments : [];

  const handlerName = request.status?.handler
    ? `${request.status.handler.lastName?.toUpperCase() ?? ''} ${request.status.handler.firstName?.toUpperCase() ?? ''}`.trim()
    : null;

  return (
    <Card
      variant="outlined"
      sx={{ 
        borderLeft: `2px solid ${borderColor}`, 
        cursor: 'pointer' 
      }}
      onClick={onClick}
    >
      {/* ── HEADER BAND ── */}
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        px={2}
        py={1}
        sx={{ backgroundColor: bandBg }}
      >
        {request.requestType && (
          <Chip
            label={request.requestType}
            size="small"
            sx={{ 
                bgcolor: borderColor, 
                color: 'text.primary', 
            }}
          />
        )}
        <Typography 
          variant="h6" 
          sx={{ flex: 1, fontWeight: 600 }}
        >
          {request.title}
        </Typography>
        <Chip
          size="small"
          icon={statusCfg.icon}
          label={statusKey}
          color={statusCfg.chipColor}
          onClick={(e) => e.stopPropagation()}
        />
      </Box>

      {/* ── INFO ROW ── */}
      <Box px={2} py={1}>
        {/* Line 1: name */}
        <Typography variant="body2" fontWeight={600}>
          {request.crewCode
            ? `${request.crewCode} - ${request.crewName?.toUpperCase()}`
            : request.crewName?.toUpperCase() || '-'}
        </Typography>
        {/* Line 2: date range + createdAt */}
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={0.5}>
            <CalendarMonth sx={{ fontSize: 13, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {format(new Date(request.startDate), 'dd/MM/yyyy')} - {format(new Date(request.endDate), 'dd/MM/yyyy')}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Created at: {format(new Date(request.createdAt), 'HH:mm dd/MM/yy')}
          </Typography>
        </Box>
        {/* Line 4: handler */}
        {handlerName && (
          <Typography variant="caption" color="text.secondary" display="block">
            Handled by <strong>{handlerName}</strong>
            {request.status?.statusDate && (
              <> at {format(new Date(request.status.statusDate), 'HH:mm dd/MM/yy')}</>
            )}
          </Typography>
        )}
      </Box>

      {/* ── CONTENT ── */}
      <CardContent sx={{ pb: 0, pt: 1 }}>
        <Box
          dangerouslySetInnerHTML={{ __html: request.content }}
          onClick={(e) => e.stopPropagation()}
          sx={{ fontSize: '0.8rem', lineHeight: 1.5, '& p': { my: 0.5 } }}
        />
      </CardContent>

      {/* ── COMMENTS (Collapse) ── */}
      <Collapse in={commentsExpanded} timeout="auto" unmountOnExit>
        <CardContent>
          <Divider sx={{ mb: 1 }} />
          <Typography 
            variant="subtitle2" 
            color="text.secondary" 
            fontWeight={600} 
            display="block" 
            mb={1}
          >
            Comments ({comments.length})
          </Typography>
          {comments.length > 0 ? (
            comments.map((c, idx) => (
              <Typography 
                key={c._id || idx} 
                variant="body2" 
                sx={{ mt: 1 }}
              >
                {idx + 1}. {c.comment}
              </Typography>
            ))
          ) : (
            <Typography 
              variant="caption" 
              color="text.secondary"
            >
              No comments available.
            </Typography>
          )}
        </CardContent>
      </Collapse>

      {/* ── ACTIONS ── */}
      <CardActions disableSpacing sx={{ px: 2, pt: 0 }}>
        <Box sx={{ ml: 'auto' }}>
          <Tooltip title={commentsExpanded ? 'Hide comments' : `Comments (${comments.length})`} arrow>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); setCommentsExpanded(p => !p); }}
              sx={{
                transform: commentsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            >
              <ExpandMore fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </CardActions>
    </Card>
  );
};

export default RequestCard;
